import { Activity, Brain, FileText, Users, HeartPulse, Stethoscope, Zap, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Hero } from '../../components/Hero';
import { AnimatedSection } from '../../components/AnimatedSection';

export function MovementDisorders() {
  return (
    <div>
      <Hero
        title="Movement Disorders"
        description="Expert diagnosis and treatment of neurological conditions affecting movement, utilizing advanced therapies and decades of specialized experience to improve motor function and enhance quality of life."
        image="https://images.unsplash.com/photo-1584515979956-d9f6e5d09982?auto=format&fit=crop&q=80"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <AnimatedSection>
          <div className="grid md:grid-cols-2 gap-12 mb-16">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Expert Movement Disorder Care</h2>
              <p className="text-gray-600 mb-6">
                Under the leadership of Dr. Jorge L. Juncos, former Director of Emory's Movement 
                Disorders Program (1988-2022), our clinic provides specialized care for a wide range 
                of movement disorders. With over four decades of experience, we offer comprehensive 
                evaluation and treatment using the latest therapeutic approaches and technologies.
              </p>
              <p className="text-gray-600">
                Our approach combines clinical expertise with cutting-edge treatments, including 
                advanced medication protocols, specialized therapy programs, and management of 
                Deep Brain Stimulation (DBS) therapy when appropriate.
              </p>
            </div>
            <div className="bg-gradient-cool rounded-lg p-8 shadow-glow">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Conditions We Treat</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <Activity className="h-6 w-6 text-blue-600 mr-3 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Parkinson's Disease</h4>
                    <p className="text-gray-600">Comprehensive management of motor and non-motor symptoms</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <Brain className="h-6 w-6 text-blue-600 mr-3 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Essential Tremor</h4>
                    <p className="text-gray-600">Advanced treatment options for tremor control</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <HeartPulse className="h-6 w-6 text-blue-600 mr-3 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Dystonia</h4>
                    <p className="text-gray-600">Specialized care for various forms of muscle contractions</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <Stethoscope className="h-6 w-6 text-blue-600 mr-3 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Atypical Parkinsonism</h4>
                    <p className="text-gray-600">Expert management of complex parkinsonian syndromes</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </AnimatedSection>

        <AnimatedSection delay={0.2}>
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">Our Diagnostic Approach</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-glow transition-shadow">
                <div className="mb-6">
                  <img
                    src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&q=80"
                    alt="Clinical Evaluation"
                    className="w-full h-40 object-cover rounded-lg mb-4"
                  />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Clinical Evaluation</h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• Detailed movement analysis</li>
                  <li>• Neurological examination</li>
                  <li>• Family history assessment</li>
                  <li>• Medication review</li>
                  <li>• Quality of life evaluation</li>
                  <li>• Non-motor symptom assessment</li>
                </ul>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-glow transition-shadow">
                <div className="mb-6">
                  <img
                    src="https://images.unsplash.com/photo-1532094349884-543bc11b234d?auto=format&fit=crop&q=80"
                    alt="Advanced Testing"
                    className="w-full h-40 object-cover rounded-lg mb-4"
                  />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Advanced Testing</h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• DaTscan imaging when indicated</li>
                  <li>• Movement quantification</li>
                  <li>• Cognitive assessment</li>
                  <li>• Gait analysis</li>
                  <li>• Balance assessment</li>
                  <li>• Functional capacity testing</li>
                </ul>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-glow transition-shadow">
                <div className="mb-6">
                  <img
                    src="https://images.unsplash.com/photo-1576765608535-5f04d1e3f289?auto=format&fit=crop&q=80"
                    alt="Treatment Planning"
                    className="w-full h-40 object-cover rounded-lg mb-4"
                  />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Treatment Planning</h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• Personalized care strategies</li>
                  <li>• Multi-disciplinary coordination</li>
                  <li>• Regular progress monitoring</li>
                  <li>• Treatment optimization</li>
                  <li>• Family education</li>
                  <li>• Long-term management plans</li>
                </ul>
              </div>
            </div>
          </div>
        </AnimatedSection>

        <AnimatedSection delay={0.4}>
          <div className="bg-gradient-healing rounded-lg p-8 mb-16 shadow-glow">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Comprehensive Treatment Options</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Medical Management</h3>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start">
                    <FileText className="h-5 w-5 text-blue-600 mr-2 mt-1" />
                    <span>Customized medication protocols</span>
                  </li>
                  <li className="flex items-start">
                    <Brain className="h-5 w-5 text-blue-600 mr-2 mt-1" />
                    <span>Regular medication adjustments</span>
                  </li>
                  <li className="flex items-start">
                    <Shield className="h-5 w-5 text-blue-600 mr-2 mt-1" />
                    <span>Side effect monitoring</span>
                  </li>
                  <li className="flex items-start">
                    <HeartPulse className="h-5 w-5 text-blue-600 mr-2 mt-1" />
                    <span>Non-motor symptom management</span>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Advanced Therapies</h3>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start">
                    <Zap className="h-5 w-5 text-blue-600 mr-2 mt-1" />
                    <span>Deep Brain Stimulation management</span>
                  </li>
                  <li className="flex items-start">
                    <Activity className="h-5 w-5 text-blue-600 mr-2 mt-1" />
                    <span>Botulinum toxin therapy</span>
                  </li>
                  <li className="flex items-start">
                    <Users className="h-5 w-5 text-blue-600 mr-2 mt-1" />
                    <span>Specialized rehabilitation programs</span>
                  </li>
                  <li className="flex items-start">
                    <Stethoscope className="h-5 w-5 text-blue-600 mr-2 mt-1" />
                    <span>Clinical trial opportunities</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </AnimatedSection>

        <AnimatedSection delay={0.6}>
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">Support Services</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-glow transition-shadow">
                <div className="mb-6">
                  <img
                    src="https://images.unsplash.com/photo-1551601651-2a8555f1a136?auto=format&fit=crop&q=80"
                    alt="Physical Therapy"
                    className="w-full h-40 object-cover rounded-lg"
                  />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Physical Therapy</h3>
                <p className="text-gray-600">
                  Specialized movement therapy programs including LSVT BIG®, balance training, 
                  gait improvement, and fall prevention strategies. Our therapists work closely 
                  with you to maintain and improve mobility and independence.
                </p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-glow transition-shadow">
                <div className="mb-6">
                  <img
                    src="https://images.unsplash.com/photo-1581056771107-24ca5f033842?auto=format&fit=crop&q=80"
                    alt="Occupational Therapy"
                    className="w-full h-40 object-cover rounded-lg"
                  />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Occupational Therapy</h3>
                <p className="text-gray-600">
                  Practical strategies for daily activities, adaptive equipment training, and 
                  home safety assessments. We focus on maintaining independence and quality 
                  of life in daily activities.
                </p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-glow transition-shadow">
                <div className="mb-6">
                  <img
                    src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&q=80"
                    alt="Education & Resources"
                    className="w-full h-40 object-cover rounded-lg"
                  />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Education & Resources</h3>
                <p className="text-gray-600">
                  Comprehensive patient and family education, support group information, and 
                  connections to community resources. We believe knowledge and support are 
                  essential components of effective care.
                </p>
              </div>
            </div>
          </div>
        </AnimatedSection>

        <AnimatedSection delay={0.8}>
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Expert Care for Movement Disorders</h2>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              With decades of experience in treating movement disorders, our team provides 
              comprehensive care using the latest treatment approaches. Let us help you 
              manage your symptoms and improve your quality of life.
            </p>
            <div className="flex justify-center gap-4">
              <Link
                to="/contact"
                className="bg-blue-600 text-white px-8 py-3 rounded-md font-semibold hover:bg-blue-700 transition-colors"
              >
                Schedule a Consultation
              </Link>
              <Link
                to="/resources/education"
                className="bg-white text-blue-600 border border-blue-600 px-8 py-3 rounded-md font-semibold hover:bg-blue-50 transition-colors"
              >
                Learn More
              </Link>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </div>
  );
}